import { useEmbeddedChatActions } from '@mm/state/actions/EmbeddedChatActions';
import JSONUtils from '@mm/state/helpers/json-utils';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { selectSuppressEmbeddedChatWidget } from '@mm/state/selectors/viewSelectors';
import { LocalStorageService } from '@mm/utils';
import sortBy from 'lodash/sortBy';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { loadScript } from '../../helpers/load-script';
import ObjectUtils from '../../helpers/utils/object-utils';
import { useDebounceWithReset } from '../../hooks/use-debounce-with-reset';

const ELIGIBLE_CHAT_WIDGET_PATHNAME_REGEX =
  /^\/chatbot-editor\/\d+\/(dashboard|x-ray|mercury|contacts|audiences|automations|integrations)/;
const eligibleChatWidgetPath = (pathname: string) => {
  return (
    ELIGIBLE_CHAT_WIDGET_PATHNAME_REGEX.test(pathname) ||
    pathname.startsWith('/report/')
  );
};
const getLLRDomain = (pathname: string) =>
  pathname.startsWith('/report/')
    ? pathname.match(/^\/report\/(?<domain>[A-Za-z.]+)$/)?.groups?.domain
    : undefined;

const promoterMeetsConditions = (promoter: PromoterModel, pathname: string) => {
  return (window as any).mmBootstrap?.testPromoterConditions(
    promoter.conditions,
    pathname,
  );
};

const matchPromoter = (
  promoters: PromoterModel[] | undefined,
  pathname: string,
): PromoterModel | undefined => {
  if (!promoters) {
    return;
  }
  for (const promoter of promoters) {
    if (promoterMeetsConditions(promoter, pathname)) {
      return promoter;
    }
  }
};

const getGreetingsClosedFromLocalStorage = (): Record<
  string,
  boolean | undefined
> => {
  return (
    JSONUtils.safeParse(
      LocalStorageService.getItem('variableChatGreetingsClosed'),
    ) || {}
  );
};

const getWidgetClosedAt = (): Record<string, string | undefined> => {
  return (
    JSONUtils.safeParse(
      LocalStorageService.getItem('variableChatWidgetClosedAt'),
    ) || {}
  );
};

const getWidgetActive = (): Record<string, string | undefined> => {
  return (
    JSONUtils.safeParse(
      LocalStorageService.getItem('variableChatWidgetActive'),
    ) || {}
  );
};

const getWidgetStateFromLocalStorage = (
  remotePageId: string,
  selectedPromoter: PromoterModel,
) => {
  const activateWindow = remotePageId && getWidgetActive()[remotePageId];
  const greetingLastActive = remotePageId && getWidgetClosedAt()[remotePageId];
  const showGreeting = (() => {
    if (
      activateWindow ||
      (selectedPromoter.searchToken &&
        getGreetingsClosedFromLocalStorage()[selectedPromoter.searchToken])
    ) {
      return false;
    }
    if (!greetingLastActive) {
      return true;
    }
    const greetingLastActiveMoment = moment(greetingLastActive);
    return (
      greetingLastActiveMoment.isValid() &&
      greetingLastActiveMoment.add(24, 'hours').isBefore()
    );
  })();

  return { activateWindow, showGreeting };
};

interface PromoterConfig {
  remotePageId: string;
  selectedPromoter: PromoterModel;
}

const useEmbeddedChatWidgetConfig = () => {
  const suppress = useSelector(selectSuppressEmbeddedChatWidget);

  const facebookPage = useSelector(activeFacebookPageSelector);
  const { id: fbPageId, subscription } = facebookPage || {};
  const { pathname } = useLocation();

  const embeddedChatActions = useEmbeddedChatActions();

  const [promoters, setPromoters] = useState<PromoterModel[]>();
  const [selectedPromoter, setSelectedPromoter] = useState<PromoterModel>();

  const [remotePageId, setRemotePageId] = useState<string>();
  const remoteUserIdRef = useRef<string>();

  const promoterConfig = useMemo<PromoterConfig | undefined>(() => {
    return suppress || !selectedPromoter || !remotePageId
      ? undefined
      : {
          selectedPromoter,
          remotePageId,
        };
  }, [selectedPromoter, suppress, remotePageId]);
  const [debouncedPromoterConfig, resetDebouncedPromoterConfig] =
    useDebounceWithReset(promoterConfig, 500);

  useEffect(() => {
    if (!remotePageId) {
      return;
    }

    const matchedPromoter = matchPromoter(promoters, pathname);

    setSelectedPromoter(oldPromoter => {
      if (!eligibleChatWidgetPath(pathname)) {
        return undefined;
      }
      if (
        oldPromoter &&
        !getWidgetStateFromLocalStorage(remotePageId, oldPromoter).showGreeting
      ) {
        return oldPromoter;
      }
      return matchedPromoter;
    });
  }, [promoters, pathname, remotePageId]);

  useEffect(() => {
    loadScript({
      id: 'mm-bootstrap',
      src: `${WEBCHAT_API_URL}bootstrap.js`,
      async: true,
    });
    loadScript({
      id: 'mm-webchat-bootstrap',
      src: `${WEBCHAT_API_URL}webchat-bootstrap.js`,
      async: true,
    });
  });

  useEffect(() => {
    (async () => {
      const configuration = await embeddedChatActions.getConfiguration(
        fbPageId,
      );
      if (configuration) {
        const sortedPromoters = sortBy(configuration.promoters, promoter => {
          if (!promoter.name || promoter.name.indexOf('#') === -1) {
            return 99999;
          }
          return Number(promoter.name.split('#')[0]) || 99999;
        });
        setRemotePageId(configuration.remotePageId);
        setPromoters(sortedPromoters);
        setSelectedPromoter(undefined);
        resetDebouncedPromoterConfig(undefined);
        remoteUserIdRef.current = undefined;
      }
    })();

    return () => {
      remoteUserIdRef.current = undefined;
      setPromoters([]);
      setRemotePageId(undefined);
      setSelectedPromoter(undefined);
      resetDebouncedPromoterConfig(undefined);
    };
  }, [fbPageId, embeddedChatActions, resetDebouncedPromoterConfig]);

  const getSenderId = useMemo(() => {
    return async () => {
      if (remoteUserIdRef.current) {
        return remoteUserIdRef.current;
      }
      const llrDomain = getLLRDomain(pathname);
      const result = await embeddedChatActions.authorize(fbPageId, llrDomain);
      if (result) {
        remoteUserIdRef.current = result.remoteUserId;
        return result.remoteUserId;
      }
    };
  }, [embeddedChatActions, fbPageId, remoteUserIdRef, pathname]);

  return {
    promoterConfig: debouncedPromoterConfig,
    getSenderId,
  };
};
export const EmbeddedChatWidget = () => {
  const embeddedChatActions = useEmbeddedChatActions();

  const { promoterConfig, getSenderId } = useEmbeddedChatWidgetConfig();

  useEffect(() => {
    if (!promoterConfig) {
      return;
    }
    const { selectedPromoter, remotePageId } = promoterConfig;

    if (selectedPromoter) {
      const { activateWindow, showGreeting } = getWidgetStateFromLocalStorage(
        remotePageId,
        selectedPromoter,
      );

      embeddedChatActions.clearStorage();

      (window as any).MMWebchat.bootstrap(remotePageId, {
        ...selectedPromoter,
        getSenderId,
        activateWindow,
        showGreeting,
        enableUploads: true,
        greeting: selectedPromoter.webchatGreeting,
        greetingWidget: selectedPromoter.webchatGreetWithStartbot
          ? ObjectUtils.deepSnakeKeys(selectedPromoter.webchatGreetingWidget)
          : undefined,
        customButtons: selectedPromoter.customButtons?.length
          ? selectedPromoter.customButtons
          : undefined,
        greetWithStartbot: selectedPromoter.webchatGreetWithStartbot,
        onActivateChatWindow: () => {
          LocalStorageService.setItem(
            'variableChatWidgetActive',
            JSON.stringify({
              ...getWidgetActive(),
              [remotePageId]: true,
            }),
          );
        },
        onCloseChatWindow: () => {
          LocalStorageService.setItem(
            'variableChatWidgetActive',
            JSON.stringify({
              ...getWidgetActive(),
              [remotePageId]: false,
            }),
          );

          LocalStorageService.setItem(
            'variableChatWidgetClosedAt',
            JSON.stringify({
              ...getWidgetClosedAt(),
              [remotePageId]: moment().utc().toISOString(),
            }),
          );
        },
        onCloseGreeting: () => {
          if (selectedPromoter.searchToken) {
            LocalStorageService.setItem(
              'variableChatGreetingsClosed',
              JSON.stringify({
                ...getGreetingsClosedFromLocalStorage(),
                [selectedPromoter.searchToken]: true,
              }),
            );
          }
        },
      });
    }
    return () => {
      document.getElementById('mm-root')?.remove();
      embeddedChatActions.clearStorage();
    };

    // Be careful with these deps -- repeatedly reinitializing the webchat widget can make it angry (not render correctly).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoterConfig]);

  return null;
};
